/**
 * blockedTasks.js - the blocked-tasks derivation.
 *
 * This file exists in TWO byte-identical copies:
 *   lambda/layer/nodejs/blockedTasks.js   (backend, via /opt/nodejs/blockedTasks)
 *   frontend/src/utils/blockedTasks.js    (frontend)
 * A jest test asserts the copies are identical - edit BOTH together.
 *
 * `require('./dependencyRefs')` resolves to each side's own sibling: the layer
 * copy finds /opt/nodejs/dependencyRefs.js, the frontend copy finds
 * frontend/src/utils/dependencyRefs.js. That is what lets the two stay
 * byte-identical despite different module roots.
 */

const { toSubtaskId } = require('./dependencyRefs');

/**
 * Build the blocked-tasks map consumed by KanbanBoard, GanttChart and
 * TaskDetailsModal (via getBlockers). Single shared implementation - the
 * link payload follows the canonical dependencyRefs contract, so
 * link.source is always a numeric ref string.
 *
 * Map key: String(taskId) of the BLOCKED task.
 * Blockers: { type: 'issue', id, title } or
 *           { type: 'dependency', id, title, parentTitle }.
 */
const buildBlockedTasksMap = (issues, links, subtasks, mainTasks) => {
    const map = new Map();

    // 1. Issue blockers. A Closed issue no longer blocks: the block lifts when
    //    the Migration Experts close it, not when the client proposes a
    //    resolution - 'Resolved by Client' is a claim an admin can reject
    //    (admin-issues:516), and lifting on it would let a client unblock their
    //    own work.
    //
    //    Filtered at READ, deliberately. The stored blockedTasks array survives
    //    the close as the record of what the issue held up, IssuesTable keeps
    //    showing it, and reopening an issue blocks again with no write.
    //
    //    Compared against 'Closed' rather than an allow-list of open statuses so
    //    that an issue with a missing or unexpected status keeps blocking.
    //    Failing toward blocked is the safe direction.
    (issues || []).forEach(issue => {
        if (issue.status === 'Closed') return;
        (issue.blockedTasks || []).forEach(taskId => {
            const key = String(taskId);
            const blockers = map.get(key) || [];
            blockers.push({ type: 'issue', id: issue.issueId, title: issue.title });
            map.set(key, blockers);
        });
    });

    // 2. Dependency blockers (FS only, where the source subtask is still open)
    (links || []).forEach(link => {
        if (link.type && link.type !== 'FS') return;

        const sourceTask = (subtasks || []).find(t => t.taskId === toSubtaskId(link.source));

        // A ref that is WELL FORMED but names no task blocks anyway.
        //
        // "9" parses fine, so a link is built for it, but ST#0009 may not
        // exist - a mis-authored template, a task deleted out from under its
        // successor. Until 2026-08-15 that contributed no blocker and said
        // nothing, so a task whose ONLY predecessor was a broken ref reported
        // itself ready to start. That is the same failure as the closed-issue
        // bug: silence read as "nothing is wrong".
        //
        // Fail toward blocked. An unresolvable predecessor is not a finished
        // one, and the fix is to repair the reference, not to start the work.
        //
        // Cross-template refs are NOT this: their source legitimately lives
        // outside this task set, so they are skipped rather than flagged.
        if (!sourceTask && !link.sourceProjectId) {
            const targetId = String(link.target);
            const blockers = map.get(targetId) || [];
            blockers.push({
                type: 'unresolved',
                id: String(link.source),
                title: `${toSubtaskId(link.source)} (referenced but not found)`
            });
            map.set(targetId, blockers);
            return;
        }

        if (sourceTask && !sourceTask.isClosed) {
            const targetId = String(link.target);
            const blockers = map.get(targetId) || [];
            const parentTask = (mainTasks || []).find(s => String(s.taskId) === String(sourceTask.mainTaskId));
            blockers.push({
                type: 'dependency',
                id: String(link.source),
                title: sourceTask.subject,
                parentTitle: parentTask?.subject
            });
            map.set(targetId, blockers);
        }
    });

    return map;
};

module.exports = {
    buildBlockedTasksMap
};
